<script setup>
import { defineProps, ref } from "vue";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";

const props = defineProps({
  userCount: {
    type: Object,
    required: true,
  },
  newsCount: {
    type: Object,
    required: true,
  },
  galleryCount: {
    type: Object,
    required: true,
  },
  dosenCount: {
    type: Object,
    required: true,
  },
  agendaCount: {
    type: Object,
    required: true,
  },
  agenda: {
    type: Object,
    required: true,
  },
  main_menu: {
    type: Array,
    required: true,
  },
});
const showModal = ref(false);
</script>

<style scoped>
.dashboard-content {
  margin-top: 2rem;
}
@media (min-width: 640px) {
  .dashboard-content {
    margin-top: 3rem;
  }
}
.dashboard-content .grid {
  margin-top: 1rem;
}
.dashboard-content .grid > article {
  transition: background-color 0.2s ease;
}
.dashboard-content .grid > article:hover {
  background-color: #f9fafb; /* Tailwind's gray-50 */
}
.dashboard-content .grid > article > div {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.dashboard-content .grid > article > div > p {
  margin: 0;
}
.dashboard-content .grid > article > div > span {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px; /* Full rounded */
  background-color: #f3f4f6; /* Tailwind's gray-200 */
}
.dashboard-content .grid > article > div > span svg {
  width: 1.25rem; /* Tailwind's w-5 */
  height: 1.25rem; /* Tailwind's h-5 */
  fill: currentColor;
}
</style>
<template>
  <Container>
    <template #header>
      <Header :menu="main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div class="container px-4 mx-auto">
        <!-- Stats Grid -->
        <div class="dashboard-content">
          <div
            class="grid grid-cols-1 gap-4 mt-4 sm:mt-6 sm:grid-cols-2 lg:grid-cols-4"
          >
            <!-- Users Count -->
            <article
              v-if="$page.props.can.master_access"
              class="p-4 bg-white border border-gray-100 rounded-lg shadow-sm sm:p-6"
            >
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-xs text-gray-500 sm:text-sm">Pengguna</p>
                  <p class="text-xl font-medium text-gray-900 sm:text-2xl">
                    {{ userCount }}
                  </p>
                </div>
                <span class="p-2 text-blue-600 bg-blue-100 rounded-full sm:p-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    class="w-5 h-5 sm:w-6 sm:h-6"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M8.25 6.75a3.75 3.75 0 1 1 7.5 0 3.75 3.75 0 0 1-7.5 0ZM15.75 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM2.25 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM6.31 15.117A6.745 6.745 0 0 1 12 12a6.745 6.745 0 0 1 6.709 7.498.75.75 0 0 1-.372.568A12.696 12.696 0 0 1 12 21.75c-2.305 0-4.47-.612-6.337-1.684a.75.75 0 0 1-.372-.568 6.787 6.787 0 0 1 1.019-4.38Z"
                      clip-rule="evenodd"
                    />
                    <path
                      d="M5.082 14.254a8.287 8.287 0 0 0-1.308 5.135 9.687 9.687 0 0 1-1.764-.44l-.115-.04a.563.563 0 0 1-.373-.487l-.01-.121a3.75 3.75 0 0 1 3.57-4.047ZM20.226 19.389a8.287 8.287 0 0 0-1.308-5.135 3.75 3.75 0 0 1 3.57 4.047l-.01.121a.563.563 0 0 1-.373.486l-.115.04c-.567.2-1.156.349-1.764.441Z"
                    />
                  </svg>
                </span>
              </div>
            </article>

            <!-- News Count -->
            <article
              v-if="$page.props.can.berita_access"
              class="p-4 bg-white border border-gray-100 rounded-lg shadow-sm sm:p-6"
            >
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-xs text-gray-500 sm:text-sm">Berita</p>
                  <p class="text-xl font-medium text-gray-900 sm:text-2xl">
                    {{ newsCount }}
                  </p>
                </div>
                <span
                  class="p-2 text-green-600 bg-green-100 rounded-full sm:p-3"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    class="w-5 h-5 sm:w-6 sm:h-6"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M4.125 3C3.089 3 2.25 3.84 2.25 4.875V18a3 3 0 0 0 3 3h15a3 3 0 0 1-3-3V4.875C17.25 3.839 16.41 3 15.375 3H4.125ZM12 9.75a.75.75 0 0 0 0 1.5h1.5a.75.75 0 0 0 0-1.5H12Zm-.75-2.25a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5H12a.75.75 0 0 1-.75-.75ZM6 12.75a.75.75 0 0 0 0 1.5h7.5a.75.75 0 0 0 0-1.5H6Zm-.75 3.75a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5H6a.75.75 0 0 1-.75-.75ZM6 6.75a.75.75 0 0 0-.75.75v3c0 .414.336.75.75.75h3a.75.75 0 0 0 .75-.75v-3A.75.75 0 0 0 9 6.75H6Z"
                      clip-rule="evenodd"
                    />
                    <path
                      d="M18.75 6.75h1.875c.621 0 1.125.504 1.125 1.125V18a1.5 1.5 0 0 1-3 0V6.75Z"
                    />
                  </svg>
                </span>
              </div>
            </article>

            <!-- Gallery Count -->
            <article
              v-if="$page.props.can.galeri_access"
              class="p-4 bg-white border border-gray-100 rounded-lg shadow-sm sm:p-6"
            >
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-xs text-gray-500 sm:text-sm">Galeri</p>
                  <p class="text-xl font-medium text-gray-900 sm:text-2xl">
                    {{ galleryCount }}
                  </p>
                </div>
                <span
                  class="p-2 text-purple-600 bg-purple-100 rounded-full sm:p-3"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    class="w-5 h-5 sm:w-6 sm:h-6"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M1.5 6a2.25 2.25 0 0 1 2.25-2.25h16.5A2.25 2.25 0 0 1 22.5 6v12a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 18V6ZM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0 0 21 18v-1.94l-2.69-2.689a1.5 1.5 0 0 0-2.12 0l-.88.879.97.97a.75.75 0 1 1-1.06 1.06l-5.16-5.159a1.5 1.5 0 0 0-2.12 0L3 16.061Zm10.125-7.81a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </span>
              </div>
            </article>

            <!-- Dosen Count -->
            <article
              v-if="$page.props.can.dosen_access"
              class="p-4 bg-white border border-gray-100 rounded-lg shadow-sm sm:p-6"
            >
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-xs text-gray-500 sm:text-sm">Dosen</p>
                  <p class="text-xl font-medium text-gray-900 sm:text-2xl">
                    {{ dosenCount }}
                  </p>
                </div>
                <span
                  class="p-2 text-indigo-600 bg-indigo-100 rounded-full sm:p-3"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    class="w-5 h-5 sm:w-6 sm:h-6"
                  >
                    <path
                      d="M11.7 2.805a.75.75 0 0 1 .6 0A60.65 60.65 0 0 1 22.83 8.72a.75.75 0 0 1-.231 1.337 49.948 49.948 0 0 0-9.902 3.912l-.003.002c-.114.06-.227.119-.34.18a.75.75 0 0 1-.707 0A50.88 50.88 0 0 0 7.5 12.173v2.176a3 3 0 0 0 1.212 2.393l.05.044a49.776 49.776 0 0 0 7.165 4.88c.415.252.875.252 1.29 0a49.776 49.776 0 0 0 7.165-4.88 3 3 0 0 0 1.212-2.393V12.17a.75.75 0 0 1 .176-.699c1.008-.84 2.078-1.718 3.238-2.654A.75.75 0 0 1 24 9.739v8.114a4.5 4.5 0 0 1-1.818 3.589 51.227 51.227 0 0 1-7.965 5.277 2.25 2.25 0 0 1-2.434 0 51.227 51.227 0 0 1-7.965-5.277A4.5 4.5 0 0 1 2 17.853V9.739a.75.75 0 0 1 .896-.75c1.16.936 2.23 1.814 3.238 2.654a.75.75 0 0 1 .176.699v.038c0 .8.301 1.55.849 2.112l.05.044a49.776 49.776 0 0 0 7.165 4.88c.415.252.875.252 1.29 0a49.776 49.776 0 0 0 7.165-4.88A3 3 0 0 0 24.75 14.386V12.17a.75.75 0 0 1 .176-.699c1.008-.84 2.078-1.718 3.238-2.654A.75.75 0 0 1 24 9.739v8.114a4.5 4.5 0 0 1-1.818 3.589 51.227 51.227 0 0 1-7.965 5.277 2.25 2.25 0 0 1-2.434 0 51.227 51.227 0 0 1-7.965-5.277A4.5 4.5 0 0 1 2 17.853V9.739a.75.75 0 0 1 .896-.75Z"
                    />
                  </svg>
                </span>
              </div>
            </article>

            <!-- Agenda Count -->
            <article
              v-if="$page.props.can.agenda_access"
              class="p-4 bg-white border border-gray-100 rounded-lg shadow-sm sm:p-6"
            >
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-xs text-gray-500 sm:text-sm">Agenda</p>
                  <p class="text-xl font-medium text-gray-900 sm:text-2xl">
                    {{ agendaCount }}
                  </p>
                </div>
                <span
                  class="p-2 text-orange-600 bg-orange-100 rounded-full sm:p-3"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    class="w-5 h-5 sm:w-6 sm:h-6"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M9 1.5H5.625c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5Zm6.61 10.936a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 14.47a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
                      clip-rule="evenodd"
                    />
                    <path
                      d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z"
                    />
                  </svg>
                </span>
              </div>
            </article>
          </div>

          <!-- Agenda Section -->
          <div class="mt-4 sm:mt-6" v-if="$page.props.can.agenda_access">
            <div
              class="p-4 bg-white border border-gray-100 rounded-lg shadow-sm sm:p-6"
            >
              <div
                class="flex flex-col items-start justify-between mb-4 sm:flex-row sm:items-center sm:mb-6"
              >
                <h2
                  class="mb-2 text-lg font-semibold text-gray-900 sm:mb-0 sm:text-xl"
                >
                  Agenda Terbaru
                </h2>
                <span
                  class="px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full sm:px-3 sm:py-1"
                >
                  {{ agenda.data?.length || 0 }} Agenda
                </span>
              </div>

              <!-- Agenda List -->
              <div
                v-if="agenda.data && agenda.data.length > 0"
                class="space-y-3"
              >
                <div
                  v-for="(item, index) in agenda.data"
                  :key="index"
                  class="flex flex-col p-3 transition-colors border border-gray-200 rounded-lg sm:flex-row sm:items-center sm:justify-between hover:bg-gray-50"
                >
                  <div class="flex-1 mb-2 sm:mb-0">
                    <h3 class="text-sm font-medium text-gray-900 sm:text-base">
                      {{ item.judul }}
                    </h3>
                    <p class="mt-1 text-xs text-gray-500 sm:text-sm">
                      Status:
                      <span
                        :class="{
                          'text-green-600': item.status === 'berlangsung',
                          'text-blue-600': item.status === 'terjadwal',
                          'text-yellow-600': item.status === 'pending',
                        }"
                        class="font-medium"
                      >
                        {{ item.status }}
                      </span>
                    </p>
                  </div>
                  <div class="flex items-center space-x-2">
                    <span
                      :class="{
                        'bg-green-100 text-green-800':
                          item.status === 'berlangsung',
                        'bg-blue-100 text-blue-800':
                          item.status === 'terjadwal',
                        'bg-yellow-100 text-yellow-800':
                          item.status === 'pending',
                      }"
                      class="px-2 py-1 text-xs font-medium rounded-full"
                    >
                      {{ item.status }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Empty State -->
              <div v-else class="py-8 text-center">
                <div
                  class="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-gray-100 rounded-full"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="w-6 h-6 text-gray-400"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
                    />
                  </svg>
                </div>
                <h3 class="mb-2 text-sm font-medium text-gray-900">
                  Tidak ada agenda
                </h3>
                <p class="text-xs text-gray-500">
                  Belum ada agenda yang terjadwal untuk saat ini.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer></template>
  </Container>
</template>
